import {
  FunctionComponent,
  memo,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import sha1 from 'sha1';
import { ConfigContext, DetailsModalContext, UserContext } from '../../../../context';
import {
  StoryCard,
  StoryCardArticleContent,
  StoryCardContentGallery,
  StoryCardType,
} from '../../../../services';
import {
  CardOpenButton,
  CARDS_TO_BLOCK,
  getCssVar,
  getStoryCardName,
  getStoryCardPreviewUrl,
  IconLabel,
  useAnalytics,
  useCardDate,
  useFreeLoginPopup,
} from '../../../../shared';

import classNames from 'classnames';
import classes from './CardContainer.module.scss';

interface CardContainerProps {
  card: StoryCard;
}

export const CardContainer: FunctionComponent<PropsWithChildren<CardContainerProps>> = memo(
  ({ card, children }) => {
    const { t } = useTranslation();

    const { logClickOpenArticle } = useAnalytics();

    const { id: cardId, storyId, type, dateToDisplay, content } = card;

    const { url, urlFormatted } = content as StoryCardArticleContent;

    const { gallery } = content as StoryCardContentGallery;

    const date = useCardDate(dateToDisplay);

    const { showPostedTime = true } = useContext(ConfigContext).config.elements.card;

    const { userInfo, userProfile } = useContext(UserContext);

    const { channelId } = userInfo.userData;

    const { id: userId, isAnonymous } = userProfile;

    const { freeLoginModal, freeLoginHandler } = useFreeLoginPopup();

    const { setDetailsModalData } = useContext(DetailsModalContext);

    const previewUrl = getStoryCardPreviewUrl(card);

    const isImageGallery = useMemo(
      () =>
        [StoryCardType.IMAGE, StoryCardType.QUOTE].includes(type) && gallery && gallery?.length > 1,
      [gallery, type]
    );

    const shouldRenderOverlay = useMemo(
      () =>
        isImageGallery ||
        [
          StoryCardType.VIDEO,
          StoryCardType.AUDIO,
          StoryCardType.PDF,
          StoryCardType.POLL,
          StoryCardType.EVENT,
        ].includes(type),
      [isImageGallery, type]
    );

    const iconColor = useMemo(
      () => (!previewUrl ? getCssVar('--card-overlay-icon-color') : '#ffffff'),
      [previewUrl]
    );

    const teaserOverlay = useMemo(() => {
      if (!shouldRenderOverlay) {
        return null;
      }

      return (
        <div
          className={classNames(classes['card__teaser-overlay'], {
            [classes['card__teaser-overlay--no-image-loaded']]: !previewUrl,
            [classes['card__teaser-overlay--wide']]: !children,
          })}
        >
          <IconLabel
            iconId={isImageGallery ? 'gallery' : type.toLowerCase()}
            iconSize={32}
            color={iconColor}
            singleColor
          />
        </div>
      );
    }, [children, iconColor, isImageGallery, previewUrl, shouldRenderOverlay, type]);

    const wideTeaser = useMemo(() => {
      return (
        <div className={classes['card__teaser']}>
          {previewUrl && (
            <>
              <div
                className={classes['card__teaser-background']}
                style={{ backgroundImage: `url(${previewUrl})` }}
              ></div>
              <img className={classes['card__teaser-image']} src={previewUrl} alt={''} />
            </>
          )}
          {teaserOverlay}
        </div>
      );
    }, [previewUrl, teaserOverlay]);

    const sideTeaser = useMemo(() => {
      return (
        <div className={classes['card__side-teaser']}>
          {previewUrl && (
            <div
              className={classes['card__side-teaser-image']}
              style={{ backgroundImage: `url(${previewUrl})` }}
            ></div>
          )}
          {teaserOverlay}
        </div>
      );
    }, [previewUrl, teaserOverlay]);

    const shouldRenderTeaser = useMemo(() => {
      return previewUrl || shouldRenderOverlay;
    }, [previewUrl, shouldRenderOverlay]);

    const shouldRenderSideTeaser = useMemo(
      () => Boolean(shouldRenderTeaser && children),
      [children, shouldRenderTeaser]
    );

    const shouldRenderWideTeaser = useMemo(
      () => Boolean(shouldRenderTeaser && !children),
      [children, shouldRenderTeaser]
    );

    const cardClickHandler = useCallback(
      (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if ((event.target as HTMLElement).tagName.toLowerCase() === 'a') {
          event.preventDefault();
        }

        if (
          CARDS_TO_BLOCK.find(
            (cardToBlock) => cardToBlock.storyId === storyId && cardToBlock.cardId === cardId
          )
        ) {
          toast.info(t('common.blocked-card'));
          return;
        }

        if (url && [StoryCardType.ARTICLE, StoryCardType.QUOTE].includes(type)) {
          if (isAnonymous && urlFormatted?.includes('www.riddle.com')) {
            freeLoginHandler();
            return;
          }

          logClickOpenArticle({
            user_Id: sha1(`${userId}`),
            channel_id: channelId,
            story_id: storyId,
            item_id: cardId,
            time_stamp: new Date().toISOString(),
          });

          window.open(urlFormatted ?? url, '_blank', 'noreferrer');
        } else {
          setDetailsModalData({ storyId, cardId });
        }
      },
      [
        cardId,
        channelId,
        freeLoginHandler,
        isAnonymous,
        logClickOpenArticle,
        setDetailsModalData,
        storyId,
        t,
        type,
        url,
        urlFormatted,
        userId,
      ]
    );

    return (
      <div
        className={classNames(`card-${cardId}`, classes['card'])}
        onClick={(event) => cardClickHandler(event)}
      >
        <CardOpenButton label={getStoryCardName({ card, t })} />

        <div
          className={classNames(classes['card__content'], {
            [classes['card__content--with-place-for-side-teaser']]: shouldRenderSideTeaser,
          })}
        >
          {shouldRenderWideTeaser && wideTeaser}
          {children}
          {showPostedTime && date && <div className={classes['card__posted-time']}>{date}</div>}
        </div>

        {shouldRenderSideTeaser && sideTeaser}

        {freeLoginModal}
      </div>
    );
  }
);
